import { makeAutoObservable, runInAction } from 'mobx';
import { getCookie } from '../utils/utils';
import ScoReflectClient from './ScoReflectClient';
import { NavigateFunction } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { AxiosError } from 'axios';
import AuthStore from './auth/AuthStore';

interface IUserData {
  ID: string;
  ShortID: string;
  PhoneNumber: string;
  Email: string | undefined;
  NickName: string | undefined;
  FirstName: string | undefined;
  LastName: string | undefined;
  Sex: number | undefined;
  Birthday: string | undefined;
  Photo: string | undefined;
  RoleID: string;
}

interface IGameData {
  ID: string;
  Time: string;
  UserID: string;
  MapID: string;
  Prize: string;
  Opened: boolean;
}

interface IWarpointGameData {
  ID: string;
  Kills: number;
  Deaths: number;
  UserID: string;
  Opened: boolean;
}

interface IBeatSaberGameData {
  ID: string;
  Score: number;
  UserID: string;
  Opened: boolean;
}

class AdminStore {
  public users: IUserData[] = [];
  public games: IGameData[] = [];
  public warpointGames: IWarpointGameData[] = [];
  public beatSaberGames: IBeatSaberGameData[] = [];
  public rating: string = "Assetto Corsa";

  constructor() {
    makeAutoObservable(this);
  }

  public async fetchUsers() {
    try {
      const response = await ScoReflectClient.request('/admin/get-users');

      if (response.status === 200) {
        const users = response.data as IUserData[];
        runInAction(() => {
          this.users = users;
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  async setRating(rating: string) {
    runInAction(() => {
      this.rating = rating;
    })
  }

  public async fetchGames() {
    try {
      const response = await ScoReflectClient.request('/admin/get-games');

      if (response.status === 200) {
        const games = response.data as IGameData[];
        runInAction(() => {
          this.games = [];
          games.forEach((game) => {
            this.games.push({
              ID: game.ID,
              MapID: game.MapID,
              UserID: game.UserID,
              Time: game.Time,
              Prize: game.Prize,
              Opened: false
            });
          });
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  public async deleteBeatsaber(id: string) {
    try {
      const response = await ScoReflectClient.request('/admin/delete-beatsaber', {
        ID: id, 
      });

      if (response.status === 200) {
        await this.fetchBeatSaberGames();
        alert('Запись удалена');
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 401) {
          await AuthStore.refresh();
          this.deleteBeatsaber(id);
        }
      }
      console.log(e);
    }
  }
  public async deleteGame(id: string) {
    try {
      const response = await ScoReflectClient.request('/admin/delete-game', {
        ID: id, 
      });

      if (response.status === 200) {
        await this.fetchGames();
        alert('Запись удалена');
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 401) {
          await AuthStore.refresh();
          this.deleteGame(id);
        }
      }
      console.log(e);
    }
  }
  public async deleteWarpoint(id: string) {
    try {
      const response = await ScoReflectClient.request('/admin/delete-warpoint', {
        ID: id, 
      });

      if (response.status === 200) {
        await this.fetchWarpointGames();
        alert('Запись удалена');
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 401) {
          await AuthStore.refresh();
          this.deleteWarpoint(id);
        }
      }
      console.log(e);
    }
  }

  public async fetchWarpointGames() {
    try {
      const response = await ScoReflectClient.request('/admin/get-games-warpoint');

      if (response.status === 200) {
        const warpointGames = response.data as IWarpointGameData[];
        runInAction(() => {
          this.warpointGames = [];
          warpointGames.forEach((game) => {
            this.warpointGames.push({
              ID: game.ID,
              Kills: game.Kills,
              Deaths: game.Deaths,
              UserID: game.UserID,
              Opened: false
            });
          });
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  public async fetchBeatSaberGames() {
    try {
      const response = await ScoReflectClient.request('/admin/get-games-beatsaber');

      if (response.status === 200) {
        const games = response.data as IBeatSaberGameData[];
        runInAction(() => {
          this.beatSaberGames = [];
          games.forEach((game) => {
            this.beatSaberGames.push({
              ID: game.ID,
              Score: game.Score,
              UserID: game.UserID,
              Opened: false
            });
          });
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  public async onClick() {
    const usersElement = document.getElementById('users') as HTMLSelectElement;
    const mapsElement = document.getElementById('maps') as HTMLSelectElement;
    const timeElement = document.getElementById('time') as HTMLInputElement;
    const prizeElement = document.getElementById('prize') as HTMLInputElement;

    try {
      const response = await ScoReflectClient.request('/admin/add-game', {
        Time: timeElement.value,
        UserID: usersElement.value,
        MapID: mapsElement.value,
        Prize: Number(prizeElement.value) 
      });

      if (response.status === 200) {
        timeElement.value = '';
        prizeElement.value = '';
        await this.fetchGames();
        alert('Игра добавлена');
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 401) {
          await AuthStore.refresh();
          this.onClick();
        }
      }
      console.log(e);
    }
  }

  public async onClickWarpoint() {
    const usersElement = document.getElementById('users') as HTMLSelectElement;
    const killsElement = document.getElementById('kills') as HTMLSelectElement;
    const deathsElement = document.getElementById('deaths') as HTMLInputElement;

    try {
      const response = await ScoReflectClient.request('/admin/add-game-warpoint', {
        UserID: usersElement.value,
        Deaths: Number(deathsElement.value),
        Kills: Number(killsElement.value)
      });

      if (response.status === 200) {
        killsElement.value = '';
        deathsElement.value = '';
        await this.fetchWarpointGames();
        alert('Игра добавлена');
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 401) {
          await AuthStore.refresh();
          this.onClickWarpoint();
        }
      }
      console.log(e);
    }
  }

  public async onClickBeatSaber() {
    const usersElement = document.getElementById('users') as HTMLSelectElement;
    const scoreElement = document.getElementById('score') as HTMLSelectElement;

    try {
      const response = await ScoReflectClient.request('/admin/add-game-beatsaber', {
        UserID: usersElement.value,
        Score: Number(scoreElement.value)
      });

      if (response.status === 200) {
        scoreElement.value = '';
        await this.fetchBeatSaberGames();
        alert('Игра добавлена');
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 401) {
          await AuthStore.refresh();
          this.onClickBeatSaber();
        }
      }
      console.log(e);
    }
  }
}

// eslint-disable-next-line
export default new AdminStore();